import React from "react";

import { Alert, Button, Icon, Input, Modal, Popconfirm, Radio, Table, Tag, Tooltip, Upload, message } from "antd";

import {
    _deleteTermContractLine,
    _getTermContractLines,
    _getTermContractLinesDownload,
    _postTermContractLinesBulk,
    _postTermContractLinesUpload,
    _putTermContractLine
} from "../../services";
import AddLineDrawer from "./add-line-drawer";
import { BulkBar, BulkModal } from "./bulk";
import { EditableBackingCell, EditableNumberCell, ProductCell } from "./line-cells";
import {
    FLAGS,
    LINES_XLSX_COLUMNS,
    LINES_XLSX_EDITABLE,
    LINES_XLSX_MANDATORY,
    bulkTarget,
    countByStatus,
    errorMessage,
    formatPct,
    marginPct,
    pctVsList,
    saveBlob,
    toNumberOrNull,
    warningLines
} from "./helpers";

const STATUS_COLORS = { created: "green", updated: "blue", restored: "cyan", unchanged: "", dropped: "orange", error: "red" };
const MUTED = "rgba(0,0,0,0.45)";
/* search + flag filters; wraps on a narrow window instead of scrolling */
const TOOLBAR_ROW = { display: "flex", flexWrap: "wrap", alignItems: "center", gap: 8, marginBottom: 12 };
const CATALOG_FLAG = "notInPrivateCatalog";

/* a row-less result (a supplier price list's "dropped" line) sorts last */
const rowPosition = ({
    result,
}) => result.row === null || result.row === undefined ? Number.MAX_SAFE_INTEGER : result.row;

/**
 * The xlsx upload's results. `warnings` is the term contract's grouped object, or a plain list of texts;
 * `keyColumn` names each row (the supplier price list passes its lineKey).
 */
export const showUploadResults = ({
    results,
    warnings,
    keyColumn = { title: "Product ID", dataIndex: "productId" },
}) => {
    const counts = countByStatus({ results });
    const warningTexts = Array.isArray(warnings) ? warnings : warningLines({ warnings });
    /* errors first, so a failed row cannot hide on page 20 */
    const rows = [...results].sort((a, b) => (a.status === "error" ? 0 : 1) - (b.status === "error" ? 0 : 1)
        || rowPosition({ result: a }) - rowPosition({ result: b }));
    Modal[counts.error ? "warning" : "success"]({
        title: `Upload: ${Object.keys(counts).map(status => `${counts[status]} ${status}`).join(", ") || "no rows"}`,
        width: 800,
        content: (
            <div>
                {warningTexts.map(line => <Alert key={line} type="warning" message={line} style={{ marginBottom: 8 }} />)}
                <Table
                    size="small"
                    rowKey={r => `${r.row}:${r.lineKey}`}
                    dataSource={rows}
                    pagination={{ pageSize: 50 }}
                    columns={[
                        { title: "Row", dataIndex: "row", width: 70 },
                        keyColumn,
                        { title: "Result", dataIndex: "status", render: status => <Tag color={STATUS_COLORS[status]}>{status}</Tag> },
                        { title: "Error", dataIndex: "error" }
                    ]}
                />
            </div>
        )
    });
};

const showBulkResult = ({
    affected,
    skipped,
}) => {
    if (!skipped.length) {
        message.success(`${affected} line${affected === 1 ? "" : "s"} updated`);
        return;
    }
    Modal.info({
        title: `${affected} updated, ${skipped.length} skipped`,
        width: 640,
        content: skipped.slice(0, 50).map(s => <p key={s.lineId}>{s.lineId}: {s.reason}</p>)
            .concat(skipped.length > 50 ? [<p key="more">… and {skipped.length - 50} more</p>] : [])
    });
};

const priceOrDash = ({
    value,
}) => value === null || value === undefined ? "—" : value;

/**
 * The lines tab's actions (add-line drawer, xlsx help, download and upload), rendered in the tab bar by the profile page.
 * `onAdded` / `onUploaded` tell it to reload the lines.
 */
export class LinesActions extends React.Component {

    state = { adding: false, uploading: false, downloading: false };

    /* Called from beforeUpload, which must return false (not this Promise) or antd uploads the file too. */
    upload = async ({
        file,
    }) => {
        this.setState({ uploading: true });
        try {
            const { data } = await _postTermContractLinesUpload({ id: this.props.contract._id, file });
            showUploadResults(data);
            this.props.onUploaded();
        } catch (e) {
            message.error(errorMessage({ error: e }));
        } finally {
            this.setState({ uploading: false });
        }
    };

    download = async () => {
        const { contract } = this.props;
        this.setState({ downloading: true });
        try {
            const { data } = await _getTermContractLinesDownload({ id: contract._id });
            saveBlob({ blob: data, fileName: `${contract.reference || contract._id}-lines.xlsx` });
        } catch (e) {
            message.error(errorMessage({ error: e }));
        } finally {
            this.setState({ downloading: false });
        }
    };

    render() {
        const { contract, enterpriseName, readOnly, onAdded } = this.props;
        const { adding, uploading, downloading } = this.state;
        return (
            <div style={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: 8 }}>
                {!readOnly && <Button type="primary" icon="plus" onClick={() => this.setState({ adding: true })}>Add line</Button>}
                {!readOnly && (
                    <Tooltip overlayStyle={{ maxWidth: 480 }} title={
                        <span>
                            Mandatory: <code>{LINES_XLSX_MANDATORY.join(", ")}</code>.
                            Optional: <code>costPrice</code>, <code>backingSellerId</code> (IST seller org id), <code>maxQuantity</code> (whole number above 0; blank = no cap); a blank cell clears it, a missing column leaves it unchanged.
                            The rest ({LINES_XLSX_COLUMNS.filter(c => !LINES_XLSX_EDITABLE.includes(c)).join(", ")}) are derived and ignored on upload.
                            Download gives every line in this layout (header only when there are none), so edit that file and upload it back.
                            Rows upsert by productId and apply at once, so re-uploading updates prices.
                        </span>
                    }>
                        <Icon type="question-circle" aria-label="xlsx format" style={{ color: MUTED }} />
                    </Tooltip>
                )}
                <Button loading={downloading} onClick={this.download}><Icon type="download" /> Download xlsx</Button>
                {!readOnly && (
                    <Upload accept=".xlsx" showUploadList={false} beforeUpload={file => { this.upload({ file }); return false; }}>
                        <Button loading={uploading}><Icon type="upload" /> Upload xlsx</Button>
                    </Upload>
                )}
                {adding && (
                    <AddLineDrawer
                        contract={contract}
                        enterpriseName={enterpriseName}
                        onAdded={onAdded}
                        onClose={() => this.setState({ adding: false })}
                    />
                )}
            </div>
        );
    }
}

/**
 * The lines tab: flag filters, search, inline edit (Enter or clicking away saves, Esc cancels), row selection with
 * bulk actions. Its add / xlsx actions are `LinesActions`, which call `reload` / `reloadAfterUpload` through a ref.
 * `onSummary` hands the page the contract-wide `summary` for its summary strip.
 */
class TermContractLines extends React.Component {

    state = {
        lines: [], total: 0, page: 1, pageSize: 50, q: "", flag: "", summary: null, flagErrors: [],
        selectedIds: [], allMatching: false, bulkAction: null, loading: false
    };

    componentDidMount() {
        this.load();
    }

    /* A new page or filter drops the selection, so a bulk action never hits rows the user can't see.
     * A plain refresh (after an inline edit) keeps it, minus lines that are no longer there. */
    load = async ({
        page = this.state.page,
        q = this.state.q,
        flag = this.state.flag,
    } = {}) => {
        const sameView = page === this.state.page && q === this.state.q && flag === this.state.flag;
        this.setState(sameView ? { loading: true } : { loading: true, page, q, flag, selectedIds: [], allMatching: false });
        try {
            const { data } = await _getTermContractLines({ id: this.props.contract._id, page, q, flag });
            const ids = new Set(data.lines.map(l => l._id));
            this.setState(state => ({
                lines: data.lines, total: data.total, pageSize: data.pageSize, summary: data.summary, flagErrors: data.flagErrors || [],
                selectedIds: state.selectedIds.filter(id => ids.has(id))
            }));
            if (this.props.onSummary) this.props.onSummary({ summary: data.summary });
        } catch (e) {
            message.error(errorMessage({ error: e }));
        } finally {
            this.setState({ loading: false });
        }
    };

    /* rejects on failure so the inline cell stays open */
    saveField = async ({
        line,
        field,
        value,
    }) => {
        const data = field === "backingSellerId" ? { backingSellerId: value || null } : { [field]: toNumberOrNull({ value }) };
        try {
            await _putTermContractLine({ id: this.props.contract._id, lineId: line._id, data });
            message.success("Saved");
            this.load();
        } catch (e) {
            message.error(errorMessage({ error: e }));
            throw e;
        }
    };

    remove = async ({
        line,
    }) => {
        try {
            await _deleteTermContractLine({ id: this.props.contract._id, lineId: line._id });
            message.success("Line removed");
            this.load();
        } catch (e) {
            message.error(errorMessage({ error: e }));
        }
    };

    runBulk = async ({
        value,
    }) => {
        const { bulkAction, selectedIds, allMatching, q, flag } = this.state;
        try {
            const { data } = await _postTermContractLinesBulk({
                id: this.props.contract._id,
                action: bulkAction,
                target: bulkTarget({ lineIds: selectedIds, allMatching, filter: { q, flag } }),
                value
            });
            this.setState({ bulkAction: null, selectedIds: [], allMatching: false });
            showBulkResult(data);
            this.load();
        } catch (e) {
            message.error(errorMessage({ error: e }));
            throw e;
        }
    };

    /* for LinesActions, via the profile's ref */
    reload = () => this.load();

    reloadAfterUpload = () => {
        this.setState({ selectedIds: [], allMatching: false });
        this.load({ page: 1 });
    };

    columns = () => {
        const { readOnly } = this.props;
        return [{
            title: "Product",
            key: "product",
            render: (_, line) => <ProductCell line={line} />
        }, {
            title: "Listing supplier",
            key: "supplier",
            render: (_, line) => (line.product && line.product.supplierCompanyName) || "-"
        }, {
            title: "List price",
            key: "listPrice",
            align: "right",
            render: (_, line) => <span style={{ color: MUTED }}>{line.product && line.product.price ? priceOrDash({ value: line.product.price.original }) : "—"}</span>
        }, {
            title: "Contract price",
            key: "price",
            align: "right",
            render: (_, line) => (
                <div>
                    <EditableNumberCell label="contract price" readOnly={readOnly} value={line.price}
                        onSave={({ value }) => this.saveField({ line, field: "price", value })}>
                        <b>{priceOrDash({ value: line.price })}</b>
                    </EditableNumberCell>
                    <div style={{ fontSize: 12, color: MUTED }}>
                        {pctVsList({ price: line.price, listPrice: line.product && line.product.price ? line.product.price.original : null })}
                    </div>
                </div>
            )
        }, {
            title: "Cost",
            key: "costPrice",
            align: "right",
            render: (_, line) => (
                <EditableNumberCell label="cost price" readOnly={readOnly} value={line.costPrice}
                    onSave={({ value }) => this.saveField({ line, field: "costPrice", value })}>
                    {priceOrDash({ value: line.costPrice })}
                </EditableNumberCell>
            )
        }, {
            title: "Margin",
            key: "margin",
            align: "right",
            render: (_, line) => {
                const margin = marginPct({ price: line.price, costPrice: line.costPrice });
                const color = margin === null ? MUTED : margin < 0 ? "#a8071a" : "#237804";
                return <span style={{ color }}>{formatPct({ value: margin })}</span>;
            }
        }, {
            title: (
                <span>
                    Backing supplier{" "}
                    <Tooltip title="The supplier we buy from to fulfil this line">
                        <Icon type="question-circle" aria-label="What is a backing supplier" style={{ color: MUTED }} />
                    </Tooltip>
                </span>
            ),
            key: "backingSeller",
            render: (_, line) => (
                <EditableBackingCell line={line} readOnly={readOnly}
                    onSave={({ value }) => this.saveField({ line, field: "backingSellerId", value })} />
            )
        }, {
            title: "Max qty",
            key: "maxQuantity",
            align: "right",
            render: (_, line) => (
                <EditableNumberCell label="max quantity" readOnly={readOnly} value={line.maxQuantity} min={1} precision={0}
                    onSave={({ value }) => this.saveField({ line, field: "maxQuantity", value })}>
                    {typeof line.maxQuantity === "number" ? line.maxQuantity.toLocaleString() : <span style={{ color: MUTED }}>No cap</span>}
                </EditableNumberCell>
            )
        }, ...(readOnly ? [] : [{
            title: "",
            key: "actions",
            width: 48,
            render: (_, line) => (
                <Popconfirm title="Remove this line? It stops pricing immediately." onConfirm={() => this.remove({ line })}>
                    <Button size="small" type="link" icon="delete" aria-label="Remove line" title="Remove line" style={{ color: MUTED }} />
                </Popconfirm>
            )
        }])];
    };

    /* A failed private-catalog check shows "?" rather than a count of 0 it never made. */
    renderChips() {
        const { summary, flag, flagErrors } = this.state;
        const counts = (summary && summary.flagCounts) || {};
        const catalogError = flagErrors.find(error => /private catalog/i.test(error));
        const chipLabel = f => f.name === CATALOG_FLAG && catalogError
            ? <Tooltip title={`Couldn't check: ${catalogError}`}>{`${f.chip} · ?`}</Tooltip>
            : `${f.chip} · ${(counts[f.name] || 0).toLocaleString()}`;
        return (
            <Radio.Group buttonStyle="solid" value={flag} onChange={e => this.load({ page: 1, flag: e.target.value })}>
                <Radio.Button value="">All</Radio.Button>
                {FLAGS.map(f => <Radio.Button key={f.name} value={f.name}>{chipLabel(f)}</Radio.Button>)}
            </Radio.Group>
        );
    }

    render() {
        const { readOnly } = this.props;
        const {
            lines, total, page, pageSize, loading, flagErrors, selectedIds, allMatching, bulkAction
        } = this.state;
        const selectedCount = allMatching ? total : selectedIds.length;
        return (
            <div>
                <div style={TOOLBAR_ROW}>
                    <Input.Search
                        allowClear
                        placeholder="Search product, SKU, ID or supplier"
                        style={{ width: 300, maxWidth: "100%" }}
                        onSearch={value => this.load({ page: 1, q: value.trim() })}
                    />
                    {this.renderChips()}
                </div>
                {flagErrors.map(error => <Alert key={error} type="warning" message={`Flags incomplete: ${error}`} style={{ marginBottom: 8 }} />)}
                {!readOnly && selectedCount > 0 && (
                    <BulkBar
                        selectedCount={selectedCount}
                        pageCount={lines.length}
                        total={total}
                        allMatching={allMatching}
                        onSelectAllMatching={() => this.setState({ allMatching: true })}
                        onAction={({ action }) => this.setState({ bulkAction: action })}
                        onClear={() => this.setState({ selectedIds: [], allMatching: false })}
                    />
                )}
                <Table
                    rowKey="_id"
                    size="small"
                    loading={loading}
                    dataSource={lines}
                    columns={this.columns()}
                    rowSelection={readOnly ? undefined : {
                        selectedRowKeys: allMatching ? lines.map(l => l._id) : selectedIds,
                        onChange: keys => this.setState({ selectedIds: keys, allMatching: false })
                    }}
                    pagination={{ current: page, pageSize, total, onChange: p => this.load({ page: p }) }}
                />
                {!readOnly && (
                    <div style={{ color: MUTED, fontSize: 12 }}>Click a price or supplier to edit it in place · Enter or clicking away saves · Esc cancels</div>
                )}
                {bulkAction && (
                    <BulkModal
                        action={bulkAction}
                        count={selectedCount}
                        onRun={this.runBulk}
                        onCancel={() => this.setState({ bulkAction: null })}
                    />
                )}
            </div>
        );
    }
}

export default TermContractLines;



// WEBPACK FOOTER //
// ./src/components/term-contract/lines.js